import { FunctionalComponent, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import style from "./style.scss";

import {
  FastForwardIcon,
  PauseIcon,
  PlayIcon,
  RewindIcon,
} from "components/icons";

const ONE_SECOND = 1;
const ONE_MINUTE = ONE_SECOND * 60;
const ONE_HOUR = ONE_MINUTE * 60;

const JUMP_SECONDS = 15;

const allAudioElements = new Set<HTMLAudioElement>();

interface Props {
  showProgressBar?: boolean;
  src: string;
}

const AudioPlayer: FunctionalComponent<Props> = (props: Props) => {
  const showProgressBar = props.showProgressBar ?? true;
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [completedSeconds, setCompletedSeconds] = useState(0);
  const [playerState, setPlayerState] = useState(PlayerState.PAUSED);

  const progressPercentage =
    Math.round((completedSeconds / totalSeconds) * 1000) / 10;

  const onFastForward = () => {
    if (!audioElementRef.current) return;
    audioElementRef.current.currentTime = Math.min(
      audioElementRef.current.currentTime + JUMP_SECONDS,
      totalSeconds
    );
  };

  const onRewind = () => {
    if (!audioElementRef.current) return;
    audioElementRef.current.currentTime = Math.max(
      audioElementRef.current.currentTime - JUMP_SECONDS,
      0
    );
  };

  useEffect(() => {
    const audioEl = audioElementRef.current;
    if (!audioEl) return;

    const onDurationChange = () => {
      setTotalSeconds(audioEl.duration);
    };
    const onTimeUpdate = () => {
      setCompletedSeconds(audioEl.currentTime);
    };
    const onPause = () => {
      setPlayerState(PlayerState.PAUSED);
    };
    const onPlay = () => {
      setPlayerState(PlayerState.PLAYING);

      allAudioElements.forEach((otherAudioEl) => {
        if (otherAudioEl !== audioEl) {
          otherAudioEl.pause();
        }
      });
    };

    allAudioElements.add(audioEl);

    audioEl.addEventListener("durationchange", onDurationChange);
    audioEl.addEventListener("loadedmetadata", onDurationChange);
    audioEl.addEventListener("pause", onPause);
    audioEl.addEventListener("play", onPlay);
    audioEl.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      allAudioElements.delete(audioEl);
      audioEl.removeEventListener("durationchange", onDurationChange);
      audioEl.removeEventListener("loadedmetadata", onDurationChange);
      audioEl.removeEventListener("pause", onPause);
      audioEl.removeEventListener("play", onPlay);
      audioEl.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, [audioElementRef]);

  return (
    <div class={style.root}>
      <div class={style.graphicContainer}>
        <div class={style.graphic}>
          <svg class={style.graphicSvg} viewBox="0 0 32 32">
            <circle
              class={style.graphicSvgCircle}
              r="16"
              cx="16"
              cy="16"
              // eslint-disable-next-line react/no-unknown-property
              stroke-dasharray={`${(progressPercentage / 100) * 100.5} 100`}
            />
          </svg>
          {playerState === PlayerState.PAUSED ? (
            <button
              class={style.playButton}
              onClick={() => audioElementRef.current?.play()}
              type="button"
            >
              <PlayIcon className={style.playButtonIcon} />
            </button>
          ) : (
            <button
              class={style.pauseButton}
              onClick={() => audioElementRef.current?.pause()}
              type="button"
            >
              <PauseIcon className={style.pauseButtonIcon} />
            </button>
          )}
          <button class={style.rewindButton} type="button" onClick={onRewind}>
            <RewindIcon className={style.rewindButtonIcon} />
          </button>
          <button
            class={style.fastForwardButton}
            type="button"
            onClick={onFastForward}
          >
            <FastForwardIcon className={style.fastForwardButtonIcon} />
          </button>
        </div>

        {showProgressBar ? (
          <div class={style.progressContainer}>
            <div class={style.timeCompleted}>
              {formatTime(completedSeconds)}
            </div>
            <div class={style.progressBar}>
              <div
                class={style.progressBarFill}
                style={{
                  width: `${progressPercentage}%`,
                }}
              ></div>
            </div>
            <div class={style.timeTotal}>{formatTime(totalSeconds)}</div>
          </div>
        ) : null}
      </div>

      <audio ref={audioElementRef} src={props.src} />
    </div>
  );
};

enum PlayerState {
  PAUSED,
  PLAYING,
}

function formatTime(totalSeconds: number) {
  if (totalSeconds < 0) {
    totalSeconds = 0;
  }

  const hours = Math.floor(totalSeconds / ONE_HOUR);
  const minutes = Math.floor((totalSeconds / ONE_MINUTE) % 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours) {
    return `${leftPad(hours)}:${leftPad(minutes)}:${leftPad(seconds)}`;
  }

  return `${leftPad(minutes)}:${leftPad(seconds)}`;
}

function leftPad(value: number) {
  if (value < 10) {
    return `0${value}`;
  }

  return `${value}`;
}

export default AudioPlayer;
